import * as React from 'react';
import {
  List,
  TextField,
  FunctionField,
  Button,
  Link,
  useDataProvider,
  useTranslate,
} from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  Typography,
  Box,
  Chip,
  Divider,
} from '@mui/material';
import moment from 'moment';
import { useSearchParams } from 'react-router-dom';
import EmptyList from '../components/misc/EmptyList';
import ResizableDatagrid from '../components/table/ResizableDatagrid';
import { getSaleById } from '../services/salesService';
import { logRefundView } from '../services/pendingRefundsService';

const entity = 'refundRequests';

const statusLabel = (status) => {
  switch (status) {
    case 'waiting':
      return { label: 'İade bekliyor', color: 'warning' };
    case 'scheduled':
      return { label: 'Planlandı', color: 'info' };
    case 'done':
      return { label: 'Tamamlandı', color: 'success' };
    case 'pending':
      return { label: 'Beklemede (eski)', color: 'warning' };
    case 'approved':
      return { label: 'Onaylandı', color: 'info' };
    case 'rejected':
      return { label: 'Reddedildi', color: 'error' };
    case 'completed':
      return { label: 'Tamamlandı', color: 'success' };
    case 'failed':
      return { label: 'Başarısız', color: 'error' };
    default:
      return { label: status || '—', color: 'default' };
  }
};

const actionLabel = (action) => {
  switch (action) {
    case 'started':
      return 'İade başlatıldı';
    case 'scheduled':
      return 'Ertesi güne planlandı';
    case 'processed':
      return 'İade işlendi';
    case 'failed':
      return 'İade hatası';
    case 'viewed':
      return 'Görüntülendi';
    default:
      return action || '—';
  }
};

const RefundDetailDialog = ({ open, onClose, record }) => {
  if (!record) return null;
  const r = record;
  const logs = Array.isArray(r.logs) ? r.logs : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>İade Detayı</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          <Typography>
            <strong>Ad Soyad:</strong> {r.userName} {r.userSurname}
          </Typography>
          <Typography>
            <strong>E-posta:</strong> {r.userEmail || '—'}
          </Typography>
          <Typography>
            <strong>Etkinlik:</strong> {r.eventName || '—'}
          </Typography>
          <Typography>
            <strong>Ürün / Bilet:</strong> {r.productInfo || '—'}
          </Typography>
          <Typography>
            <strong>PNR:</strong> {r.ticketPnr || '—'}
          </Typography>
          <Typography>
            <strong>Tutar:</strong> {r.price != null ? `${r.price} TL` : '—'}
          </Typography>
          <Typography>
            <strong>Bilet alım tarihi:</strong>{' '}
            {r.ticketCreated ? moment(r.ticketCreated).format('LLLL') : '—'}
          </Typography>
          <Typography>
            <strong>Etkinlik tarihi:</strong>{' '}
            {r.eventSession ? moment(r.eventSession).format('LLLL') : '—'}
          </Typography>
          <Typography>
            <strong>Talep tarihi:</strong>{' '}
            {r.createdate ? moment(r.createdate).format('LLLL') : '—'}
          </Typography>
          <Typography>
            <strong>Talep eden:</strong> {r.createdby || r.userEmail || '—'}
          </Typography>
          <Typography>
            <strong>Durum:</strong> {statusLabel(r.status).label}
          </Typography>
          {r.processedBy && (
            <Typography>
              <strong>İade eden:</strong> {r.processedBy}
              {r.processedAt ? ` — ${moment(r.processedAt).format('LLLL')}` : ''}
            </Typography>
          )}
          {r.lastError && (
            <Typography color="error">
              <strong>Son hata:</strong> {r.lastError}
            </Typography>
          )}
          {r.reason && (
            <Typography>
              <strong>Not:</strong> {r.reason}
            </Typography>
          )}

          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2">İşlem geçmişi</Typography>
          {logs.length === 0 ? (
            <Typography color="text.secondary" variant="body2">
              Log yok
            </Typography>
          ) : (
            [...logs].reverse().map((entry, idx) => (
              <Box
                key={`${entry.at}-${idx}`}
                sx={{
                  p: 1,
                  bgcolor: '#fafafa',
                  borderRadius: 1,
                  border: '1px solid #eee',
                }}
              >
                <Typography variant="body2">
                  <strong>{actionLabel(entry.action)}</strong>
                  {' — '}
                  {entry.at ? moment(entry.at).format('LLL') : '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Kim: {entry.actor || '—'}
                </Typography>
                {entry.detail && <Typography variant="body2">{entry.detail}</Typography>}
                {entry.error && (
                  <Typography variant="body2" color="error">
                    Hata: {entry.error}
                  </Typography>
                )}
              </Box>
            ))
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose}>Kapat</MuiButton>
      </DialogActions>
    </Dialog>
  );
};

const DetailButton = ({ record, autoOpen }) => {
  const translate = useTranslate();
  const [open, setOpen] = React.useState(Boolean(autoOpen));
  const [liveRecord, setLiveRecord] = React.useState(record);
  const dataProvider = useDataProvider();

  React.useEffect(() => {
    setLiveRecord(record);
  }, [record]);

  React.useEffect(() => {
    if (autoOpen) setOpen(true);
  }, [autoOpen]);

  React.useEffect(() => {
    if (!open || !record?.id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        await logRefundView({
          refundRequestId: record.id,
          paymentId: record.paymentId || undefined,
          saleId: record.saleId || undefined,
          source: 'detail',
        });
        const refreshed = await dataProvider.getOne('refundRequests', {
          id: record.id,
        });
        if (!cancelled && refreshed?.data) setLiveRecord(refreshed.data);
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, record?.id, record?.paymentId, record?.saleId, dataProvider]);

  if (!record) return null;
  return (
    <>
      <Button
        label={translate('refund.view_detail', { _: 'Detay Gör' })}
        onClick={() => setOpen(true)}
      />
      <RefundDetailDialog
        open={open}
        onClose={() => setOpen(false)}
        record={liveRecord || record}
      />
    </>
  );
};

const resolvePaymentId = (record) => {
  if (!record) return null;
  if (record.paymentId) return String(record.paymentId);
  const salePayment = record.body?.ticket?.sale;
  if (typeof salePayment === 'object' && salePayment?.payment) {
    const p = salePayment.payment;
    return typeof p === 'string' ? p : (p?.id ?? null);
  }
  return null;
};

const ViewRefundButton = ({ record }) => {
  const translate = useTranslate();
  const [paymentId, setPaymentId] = React.useState(() => resolvePaymentId(record));
  const [loading, setLoading] = React.useState(!resolvePaymentId(record) && !!record?.saleId);

  React.useEffect(() => {
    if (!record) return undefined;
    const direct = resolvePaymentId(record);
    if (direct) {
      setPaymentId(direct);
      setLoading(false);
      return undefined;
    }
    if (!record.saleId) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const sale = await getSaleById(record.saleId);
        const payment = sale?.payment;
        const id = typeof payment === 'string' ? payment : payment?.id;
        if (!cancelled && id) setPaymentId(String(id));
      } catch {
        // ignore
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [record]);

  if (!record) return null;
  if (loading) return <span style={{ color: '#999' }}>...</span>;
  if (!paymentId) return <span style={{ color: '#999' }}>—</span>;

  return (
    <Button
      label={translate('refund.view_refund', { _: 'İadeyi Gör' })}
      component={Link}
      to={`/payments/${paymentId}/show`}
    />
  );
};

export const RefundRequestList = (props) => {
  const translate = useTranslate();
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');

  return (
    <List
      {...props}
      empty={<EmptyList entity={entity} />}
      exporter={false}
      sort={{ field: 'createdate', order: 'DESC' }}
      perPage={50}
      title={translate('resources.refundRequests.name', { _: 'İade Talepleri' })}
      filter={{ source: 'customer' }}
    >
      <ResizableDatagrid
        storageKey="gk-datagrid-widths:refundRequests"
        bulkActionButtons={false}
        rowStyle={(record) =>
          highlightId && record?.id === highlightId
            ? { backgroundColor: 'rgba(237,108,2,0.12)' }
            : undefined
        }
      >
        <FunctionField
          label={translate('resources.refundRequests.fields.status', { _: 'Durum' })}
          render={(r) => {
            const s = statusLabel(r.status);
            return <Chip size="small" label={s.label} color={s.color} />;
          }}
        />
        <FunctionField
          label={translate('resources.refundRequests.fields.user', { _: 'Kullanıcı' })}
          render={(r) => `${r.userName || ''} ${r.userSurname || ''}`.trim() || '—'}
        />
        <TextField
          source="userEmail"
          label={translate('resources.refundRequests.fields.userEmail', { _: 'E-posta' })}
        />
        <TextField
          source="eventName"
          label={translate('resources.refundRequests.fields.eventName', { _: 'Etkinlik' })}
        />
        <TextField
          source="productInfo"
          label={translate('resources.refundRequests.fields.productInfo', { _: 'Bilet / Ürün' })}
        />
        <TextField
          source="ticketPnr"
          label={translate('resources.refundRequests.fields.ticketPnr', { _: 'PNR' })}
        />
        <FunctionField
          label={translate('resources.refundRequests.fields.createdate', { _: 'İade Tarihi' })}
          render={(r) => (r.createdate ? moment(r.createdate).format('LLL') : '—')}
        />
        <FunctionField
          label={translate('resources.refundRequests.fields.processedBy', { _: 'İade eden' })}
          render={(r) => r.processedBy || '—'}
        />
        <FunctionField
          label=""
          render={(r) => (
            <DetailButton record={r} autoOpen={Boolean(highlightId && r.id === highlightId)} />
          )}
        />
        <FunctionField label="" render={(r) => <ViewRefundButton record={r} />} />
      </ResizableDatagrid>
    </List>
  );
};
